<template>
    <div class="dqa-wrapper">
        <div class="dqa-content">
            <div class="dqa-integrated-actions">
                <div class="dqa-action-group-title">통합 실행</div>
                <div class="dqa-action-section dqa-integrated-section">
                    <div class="dqa-section-header">
                        <div class="dqa-section-title">통합 실행</div>
                        <div class="dqa-section-desc">감지, 보정, 재집계를 한번에 실행합니다</div>
                    </div>
                    <div class="dqa-action-controls">
                        <button class="dqa-action-btn dqa-integrated-btn" :disabled="integratedLoading"
                            @click="handleIntegrated">
                            {{ integratedLoading ? '실행 중...' : '통합 실행' }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="dqa-individual-actions">
                <div class="dqa-action-group-title">개별 작업</div>
                <div class="dqa-action-section">
                    <div class="dqa-section-header">
                        <div class="dqa-section-title">결측 감지</div>
                        <div class="dqa-section-desc">선택한 날짜의 데이터 결측을 감지합니다</div>
                    </div>
                    <div class="dqa-action-controls">
                        <button class="dqa-action-btn dqa-primary-btn" :disabled="detectLoading" @click="handleDetect">
                            {{ detectLoading ? '실행 중...' : '감지 실행' }}
                        </button>
                    </div>
                </div>

                <div class="dqa-action-section">
                    <div class="dqa-section-header">
                        <div class="dqa-section-title">데이터 보정</div>
                        <div class="dqa-section-desc">감지된 결측 데이터를 보정합니다</div>
                    </div>
                    <div class="dqa-action-controls">
                        <button class="dqa-action-btn dqa-primary-btn" :disabled="correctLoading"
                            @click="handleCorrect">
                            {{ correctLoading ? '실행 중...' : '보정 실행' }}
                        </button>
                    </div>
                </div>

                <div class="dqa-action-section">
                    <div class="dqa-section-header">
                        <div class="dqa-section-title">재집계 요청</div>
                        <div class="dqa-section-desc">데이터 재집계를 실행합니다</div>
                    </div>
                    <div class="dqa-action-controls">
                        <button class="dqa-action-btn dqa-primary-btn" :disabled="recalcLoading"
                            @click="handleRecalculate">
                            {{ recalcLoading ? '실행 중...' : '재집계 실행' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- 통합 실행 결과 모달 -->
        <b-modal v-model="showIntegratedModal" title="통합 실행 결과" size="sm" hide-footer>
            <div v-if="integratedResult" class="dqa-modal-content">
                <div class="dqa-result-metrics">
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">날짜</span>
                        <span class="dqa-result-value">{{ formatDate(integratedResult.date) }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">전체 상태</span>
                        <span class="dqa-result-value"
                            :class="integratedResult.summary?.overallStatus === 'completed' ? 'dqa-highlight' : ''">
                            {{ integratedResult.summary?.overallStatus === 'completed' ? '완료' :
                                integratedResult.summary?.overallStatus || '대기' }}
                        </span>
                    </div>
                    <div class="dqa-result-divider"></div>
                    <div class="dqa-result-section-title">감지 단계</div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">전체 측정점</span>
                        <span class="dqa-result-value">{{ integratedResult.steps?.detect?.totalPoints || 0 }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">결측 측정점</span>
                        <span class="dqa-result-value dqa-highlight">{{ integratedResult.steps?.detect?.pointsWithGaps
                            || 0 }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">총 결측 수</span>
                        <span class="dqa-result-value dqa-highlight">{{ integratedResult.steps?.detect?.totalGaps || 0
                        }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">시간 결측</span>
                        <span class="dqa-result-value">{{ integratedResult.steps?.detect?.timeGaps || 0 }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">이상치</span>
                        <span class="dqa-result-value">{{ integratedResult.steps?.detect?.valueAnomalies || 0 }}</span>
                    </div>
                    <div class="dqa-result-divider"></div>
                    <div class="dqa-result-section-title">보정 단계</div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">상태</span>
                        <span class="dqa-result-value">{{ integratedResult.steps?.correct?.status === 'completed' ? '완료'
                            : integratedResult.steps?.correct?.status === 'skipped' ? '건너뜀' : '대기' }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">보정 완료</span>
                        <span class="dqa-result-value dqa-highlight">{{ integratedResult.steps?.correct?.corrected || 0
                        }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">건너뜀</span>
                        <span class="dqa-result-value">{{ integratedResult.steps?.correct?.skipped || 0 }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">오류</span>
                        <span class="dqa-result-value"
                            :class="integratedResult.steps?.correct?.errors > 0 ? 'dqa-error' : ''">
                            {{ integratedResult.steps?.correct?.errors || 0 }}
                        </span>
                    </div>
                    <div class="dqa-result-divider"></div>
                    <div class="dqa-result-section-title">재집계 단계</div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">상태</span>
                        <span class="dqa-result-value">{{ integratedResult.steps?.recalc?.status === 'completed' ? '완료'
                            : '대기' }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">등록 완료</span>
                        <span class="dqa-result-value dqa-highlight">{{ integratedResult.steps?.recalc?.queued || 0
                        }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">건너뜀</span>
                        <span class="dqa-result-value">{{ integratedResult.steps?.recalc?.skipped || 0 }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">오류</span>
                        <span class="dqa-result-value"
                            :class="integratedResult.steps?.recalc?.errors > 0 ? 'dqa-error' : ''">
                            {{ integratedResult.steps?.recalc?.errors || 0 }}
                        </span>
                    </div>
                    <div v-if="integratedResult.summary?.totalProcessingTime" class="dqa-result-item">
                        <span class="dqa-result-label">처리 시간</span>
                        <span class="dqa-result-value">{{ (integratedResult.summary.totalProcessingTime /
                            1000).toFixed(2) }}초</span>
                    </div>
                </div>
            </div>
        </b-modal>

        <!-- 감지 실행 결과 모달 -->
        <b-modal v-model="showDetectModal" title="감지 실행 결과" size="sm" hide-footer>
            <div v-if="detectResult" class="dqa-modal-content">
                <div class="dqa-result-metrics">
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">날짜</span>
                        <span class="dqa-result-value">{{ formatDate(detectResult.date) }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">전체 측정점</span>
                        <span class="dqa-result-value">{{ detectResult.summary?.totalPoints || 0 }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">결측 측정점</span>
                        <span class="dqa-result-value dqa-highlight">{{ detectResult.summary?.pointsWithGaps || 0
                        }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">정상 측정점</span>
                        <span class="dqa-result-value">{{ detectResult.summary?.normalPoints || 0 }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">총 결측 수</span>
                        <span class="dqa-result-value dqa-highlight">{{ detectResult.summary?.totalGaps || 0 }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">시간 결측 수</span>
                        <span class="dqa-result-value">{{ detectResult.summary?.totalTimeGaps || 0 }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">이상치 수</span>
                        <span class="dqa-result-value">{{ detectResult.summary?.totalValueAnomalies || 0 }}</span>
                    </div>
                </div>
                <div v-if="detectResult.points && detectResult.points.length > 0" class="dqa-points-section">
                    <div class="dqa-points-header">측정점별 상세</div>
                    <div class="dqa-points-list">
                        <div v-for="(point, index) in detectResult.points" :key="index" class="dqa-point-item">
                            <div class="dqa-point-addr">{{ point.ptAddr }}</div>
                            <div class="dqa-point-details">
                                <div class="dqa-point-detail-item">
                                    <span class="dqa-point-detail-label">수집건수</span>
                                    <span class="dqa-point-detail-value">{{ point.totalRecords || 0 }}</span>
                                </div>
                                <div class="dqa-point-detail-item">
                                    <span class="dqa-point-detail-label">결측 수</span>
                                    <span class="dqa-point-detail-value"
                                        :class="point.totalGaps > 0 ? 'dqa-highlight' : ''">{{ point.totalGaps || 0
                                        }}</span>
                                </div>
                                <div class="dqa-point-detail-item">
                                    <span class="dqa-point-detail-label">시간 결측</span>
                                    <span class="dqa-point-detail-value">{{ point.timeGaps || 0 }}</span>
                                </div>
                                <div class="dqa-point-detail-item">
                                    <span class="dqa-point-detail-label">이상치</span>
                                    <span class="dqa-point-detail-value"
                                        :class="point.valueAnomalies > 0 ? 'dqa-error' : ''">{{ point.valueAnomalies ||
                                            0 }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </b-modal>

        <!-- 보정 실행 결과 모달 -->
        <b-modal v-model="showCorrectModal" title="보정 실행 결과" size="sm" hide-footer>
            <div v-if="correctResult" class="dqa-modal-content">
                <div class="dqa-result-metrics">
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">날짜</span>
                        <span class="dqa-result-value">{{ formatDate(correctResult.date) }}</span>
                    </div>
                    <div class="dqa-result-divider"></div>
                    <div class="dqa-result-section-title">감지 결과</div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">전체 측정점</span>
                        <span class="dqa-result-value">{{ correctResult.detect?.totalPoints || 0 }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">결측 측정점</span>
                        <span class="dqa-result-value dqa-highlight">{{ correctResult.detect?.pointsWithGaps || 0
                        }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">총 결측 수</span>
                        <span class="dqa-result-value dqa-highlight">{{ correctResult.detect?.totalGaps || 0 }}</span>
                    </div>
                    <div class="dqa-result-divider"></div>
                    <div class="dqa-result-section-title">보정 결과</div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">보정 완료</span>
                        <span class="dqa-result-value dqa-highlight">{{ correctResult.correct?.corrected || 0 }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">건너뜀</span>
                        <span class="dqa-result-value">{{ correctResult.correct?.skipped || 0 }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">오류</span>
                        <span class="dqa-result-value" :class="correctResult.correct?.errors > 0 ? 'dqa-error' : ''">
                            {{ correctResult.correct?.errors || 0 }}
                        </span>
                    </div>
                </div>
            </div>
        </b-modal>

        <!-- 재집계 실행 결과 모달 -->
        <b-modal v-model="showRecalcModal" title="재집계 실행 결과" size="sm" hide-footer>
            <div v-if="recalcResult" class="dqa-modal-content">
                <div class="dqa-result-metrics">
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">날짜</span>
                        <span class="dqa-result-value">{{ formatDate(recalcResult.date) }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">등록 완료</span>
                        <span class="dqa-result-value dqa-highlight">{{ recalcResult.queued || 0 }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">건너뜀</span>
                        <span class="dqa-result-value">{{ recalcResult.skipped || 0 }}</span>
                    </div>
                    <div class="dqa-result-item">
                        <span class="dqa-result-label">오류</span>
                        <span class="dqa-result-value" :class="recalcResult.errors > 0 ? 'dqa-error' : ''">
                            {{ recalcResult.errors || 0 }}
                        </span>
                    </div>
                    <div v-if="recalcResult.requestIds && recalcResult.requestIds.length > 0" class="dqa-result-item">
                        <span class="dqa-result-label">요청 ID</span>
                        <span class="dqa-result-value">{{ recalcResult.requestIds.join(', ') }}</span>
                    </div>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import backEndApi from "@api/backEndApi.js";

export default {
    name: "DataQualityActions",
    props: {
        selectedDate: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            detectLoading: false,
            correctLoading: false,
            recalcLoading: false,
            integratedLoading: false,
            detectResult: null,
            correctResult: null,
            recalcResult: null,
            integratedResult: null,
            showDetectModal: false,
            showCorrectModal: false,
            showRecalcModal: false,
            showIntegratedModal: false
        };
    },
    methods: {
        async handleDetect() {
            if (!this.selectedDate) {
                alert('날짜를 선택해주세요');
                return;
            }
            this.detectLoading = true;
            this.detectResult = null;
            try {
                const response = await backEndApi.dataQuality.postDetectDataQuality(this.selectedDate);
                if (response.data && response.data.success) {
                    this.detectResult = response.data;
                    this.showDetectModal = true;
                } else {
                    alert('감지 실행 실패: ' + (response.data?.message || '알 수 없는 오류'));
                }
            } catch (error) {
                console.error("감지 실행 실패:", error);
                alert('감지 실행 중 오류가 발생했습니다');
            } finally {
                this.detectLoading = false;
            }
        },
        async handleCorrect() {
            if (!this.selectedDate) {
                alert('날짜를 선택해주세요');
                return;
            }
            this.correctLoading = true;
            this.correctResult = null;
            try {
                const response = await backEndApi.dataQuality.postCorrectDataQuality(this.selectedDate, 'N');
                if (response.data && response.data.success) {
                    this.correctResult = response.data;
                    this.showCorrectModal = true;
                    this.$emit('action-executed');
                } else {
                    alert('보정 실행 실패: ' + (response.data?.message || '알 수 없는 오류'));
                }
            } catch (error) {
                console.error("보정 실행 실패:", error);
                alert('보정 실행 중 오류가 발생했습니다');
            } finally {
                this.correctLoading = false;
            }
        },
        async handleRecalculate() {
            if (!this.selectedDate) {
                alert('날짜를 선택해주세요');
                return;
            }
            this.recalcLoading = true;
            this.recalcResult = null;
            try {
                const response = await backEndApi.dataQuality.postRecalculateDataQuality(
                    this.selectedDate
                );
                if (response.data && response.data.success) {
                    this.recalcResult = response.data;
                    this.showRecalcModal = true;
                    this.$emit('action-executed');
                } else {
                    alert('재집계 실행 실패: ' + (response.data?.message || '알 수 없는 오류'));
                }
            } catch (error) {
                console.error("재집계 실행 실패:", error);
                alert('재집계 실행 중 오류가 발생했습니다');
            } finally {
                this.recalcLoading = false;
            }
        },
        async handleIntegrated() {
            if (!this.selectedDate) {
                alert('날짜를 선택해주세요');
                return;
            }
            this.integratedLoading = true;
            this.integratedResult = null;
            try {
                const response = await backEndApi.dataQuality.postRecalculateDataQualityAtOnce(
                    this.selectedDate,
                    'N'
                );
                if (response.data && response.data.success) {
                    this.integratedResult = response.data;
                    this.showIntegratedModal = true;
                    this.$emit('action-executed');
                } else {
                    alert('통합 실행 실패: ' + (response.data?.message || '알 수 없는 오류'));
                }
            } catch (error) {
                console.error("통합 실행 실패:", error);
                alert('통합 실행 중 오류가 발생했습니다');
            } finally {
                this.integratedLoading = false;
            }
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            // "20251127" 형식 또는 "2025-11-27" 형식 처리
            if (dateStr.length === 8) {
                const year = dateStr.substring(0, 4);
                const month = dateStr.substring(4, 6);
                const day = dateStr.substring(6, 8);
                return `${year}년 ${parseInt(month)}월 ${parseInt(day)}일`;
            } else if (dateStr.includes('-')) {
                const [year, month, day] = dateStr.split('-');
                return `${year}년 ${parseInt(month)}월 ${parseInt(day)}일`;
            }
            return dateStr;
        }
    }
};
</script>

<style scoped>
.dqa-wrapper {
    height: 100%;
    overflow: auto;
    padding: 16px;
}

.dqa-content {
    display: flex;
    flex-direction: column;
    gap: 0;
}

.dqa-action-group-title {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    padding-bottom: 8px;
    border-bottom: 2px solid #e2e8f0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.dqa-integrated-actions {
    padding-bottom: 15px;
}


.dqa-action-section {
    border-bottom: 1px solid #f1f5f9;
    margin-top: 13px;
}



.dqa-integrated-section {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    padding: 15px 15px 8px 15px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.dqa-section-header {
    margin-bottom: 12px;
}

.dqa-section-title {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 4px;
}

.dqa-section-desc {
    font-size: 11px;
    color: #6b7280;
    line-height: 1.4;
}

.dqa-action-controls {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 5px;
}

.dqa-action-btn {
    padding: 10px 16px;
    border: none;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    width: 100%;
}

.dqa-action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.dqa-primary-btn {
    background: linear-gradient(135deg, #475569 0%, #334155 100%);
    color: #ffffff;
    box-shadow: 0 2px 4px rgba(71, 85, 105, 0.2);
}

.dqa-primary-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, #334155 0%, #1e293b 100%);
    box-shadow: 0 4px 8px rgba(71, 85, 105, 0.3);
    transform: translateY(-1px);
}

.dqa-integrated-btn {
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    color: #ffffff;
    box-shadow: 0 2px 4px rgba(15, 23, 42, 0.2);
    font-weight: 700;
}

.dqa-integrated-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.4);
    transform: translateY(-1px);
}

.dqa-checkbox-group {
    margin-bottom: 4px;
}

.dqa-checkbox-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: #374151;
    cursor: pointer;
}

.dqa-checkbox-label input[type="checkbox"] {
    width: 14px;
    height: 14px;
    cursor: pointer;
}

.dqa-input-group {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

.dqa-input-label {
    font-size: 11px;
    color: #374151;
    min-width: 50px;
}

.dqa-priority-input {
    flex: 1;
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 12px;
}

.dqa-priority-input:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
}

.dqa-result-box {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 12px;
    margin-top: 8px;
}

.dqa-result-header {
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
}

.dqa-result-metrics {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.dqa-result-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 11px;
}

.dqa-result-label {
    color: #6b7280;
}

.dqa-result-value {
    font-weight: 600;
    color: #111827;
}

.dqa-result-value.dqa-highlight {
    color: #3b82f6;
}

.dqa-result-value.dqa-error {
    color: #dc2626;
}

.dqa-result-divider {
    height: 1px;
    background: #e5e7eb;
    margin: 8px 0;
}

.dqa-result-section-title {
    font-size: 11px;
    font-weight: 700;
    color: #374151;
    margin: 8px 0 4px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.dqa-points-section {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e5e7eb;
}

.dqa-points-header {
    font-size: 11px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
}

.dqa-points-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 100px;
    overflow-y: auto;
    padding-right: 4px;
}

.dqa-points-list::-webkit-scrollbar {
    width: 6px;
}

.dqa-points-list::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
}

.dqa-points-list::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

.dqa-points-list::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

.dqa-point-item {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 8px;
}

.dqa-point-addr {
    font-size: 11px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 6px;
}

.dqa-point-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.dqa-point-detail-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 10px;
}

.dqa-point-detail-label {
    color: #6b7280;
}

.dqa-point-detail-value {
    font-weight: 600;
    color: #111827;
}

.dqa-modal-content {
    padding: 8px 0;
}

.dqa-modal-content .dqa-points-list {
    max-height: 300px;
}
</style>